import { createSelector } from 'reselect';
import sortBy from 'ramda/es/sortBy';
import path from 'ramda/es/path';

import { CAFETERIA_TYPE_GLOLIME } from 'app/constants/apps/cafeteria';
import get from 'app/common/lib/get';
import i18n from 'app/i18n';
import { APP_CAFETERIA, APP_DNEVNIK } from 'app/common/constants/services';
import unreadEventsCountById from 'app/selectors/events/unread';
import { toFixedValueCurrency } from 'app/common/lib/toFixedValue';
import { normalizeName, compareStudents } from 'app/lib/compareNames';
import { selectCurrencySign } from 'app/common/components-next/currency';


const sortByName = sortBy(path(['name', 'first']));

const getPathInfo = createSelector(
  (state, props) => get(props, 'location.pathname', ''),
  (pathname) => {
    const arrPath = pathname.split('/');
    return ({
      category: arrPath[1],
      studentId: arrPath[2],
      app: arrPath[3],
      arrPath,
    });
  }
);

const selectStudents = createSelector(
  [
    state => state.students.data,
    state => get(state, 'eljur.user.result.relations.students', {}),
    getPathInfo,
    state => state.cafeteria.balance,
    unreadEventsCountById,
    state => state.user,
    selectCurrencySign,
  ],
  (commonStudents, externalStudents, pathInfo, balance, unreadEvents, user, currency) => {
    // Массив id оставшихся после сравнения учеников из внешнего сервиса
    let remainingExternalKeys = Object.keys(externalStudents);

    // Нормализированные ученики внешнего сервиса
    const normalizedExternals = remainingExternalKeys.map(key => ({
      id: key,
      normalizedFirst: normalizeName(externalStudents[key].firstname),
      normalizedLast: normalizeName(externalStudents[key].lastname),
      normalizedGender: (typeof externalStudents[key].gender === 'string')
        ? externalStudents[key].gender.trim().toLowerCase()
        : '',
    }));

    // # Обход собственных учеников кабинета
    const ownedStudents = Object.keys(commonStudents).map((key) => {
      // --- Нормализация текущего
      const normalizedCurrent = {
        id: key,
        normalizedFirst: normalizeName(commonStudents[key].name.first),
        normalizedLast: normalizeName(commonStudents[key].name.last),
        normalizedGender: (typeof commonStudents[key].gender === 'string')
          ? commonStudents[key].gender.trim().toLowerCase()
          : '',
      };

      // --- Поиск совпадений по имени/полу
      const suitableExternalId = (
        normalizedExternals.find(ext => compareStudents(normalizedCurrent, ext)) || {}
      ).id;

      // --- Фильтр оставшихся внешних на найденного ученика
      if (suitableExternalId) {
        remainingExternalKeys = remainingExternalKeys.filter(id => (id !== suitableExternalId));
      }

      const balanceValue = get(balance, ['data', 'students', key], null);
      const isBalance = (commonStudents[key].hasCafeteria
        && commonStudents[key].cafeteriaExternalServiceType !== CAFETERIA_TYPE_GLOLIME);

      return ({
        id: key,
        name: { ...commonStudents[key].name },
        gender: commonStudents[key].gender,
        photo: commonStudents[key].photo,
        showBalance: pathInfo.app === APP_CAFETERIA && pathInfo.category === 'apps' && isBalance,
        balanceValue: (isBalance && (balanceValue || balanceValue === 0)
          ? `${toFixedValueCurrency(balanceValue, false, currency)}`
          : ''),
        events: pathInfo.category === 'events' ? unreadEvents[key] : 0,
        externalId: commonStudents[key].dnevnikExternalService ? (suitableExternalId || 'not-found') : null,
        active: pathInfo.studentId === key,
        url: pathInfo.arrPath.map((item, i) => (i === 2 ? key : item)).join('/'),
        showExternalLogo: pathInfo.app === APP_DNEVNIK && commonStudents[key].dnevnikExternalService,
        currency,
        camps: commonStudents[key].camps,
      });
    });

    const ownedStudentsSorted = sortByName(ownedStudents);

    // # Обход оставшихся учеников внешнего сервиса (если приложение "дневник")
    const remainingStudents = pathInfo.app === APP_DNEVNIK ? remainingExternalKeys.map(key => ({
      id: key,
      name: {
        first: externalStudents[key].firstname,
        last: externalStudents[key].lastname,
        middle: externalStudents[key].middlename,
      },
      gender: externalStudents[key].gender,
      showBalance: pathInfo.app === APP_CAFETERIA,
      balanceValue: '',
      events: 0,
      externalId: key,
      active: pathInfo.studentId === key,
      url: `/apps/${key}/dnevnik/home`,
      showExternalLogo: true,
      currency,
    })) : [];

    // # Сборка результата
    return [
      // Сам пользователь в начале списка, для раздела событий
      ...(pathInfo.category === 'events'
        ? [{
          id: 'user',
          name: { first: i18n.t('app:user_selector_own') },
          gender: user.gender,
          photo: user.avatar,
          showBalance: false,
          balanceValue: '',
          events: unreadEvents.user || 0,
          externalId: null,
          active: pathInfo.studentId === 'user',
          url: pathInfo.arrPath.map((item, i) => (i === 2 ? 'user' : item)).join('/'),
          showExternalLogo: false,
          currency,
        }]
        : []),
      ...ownedStudentsSorted,
      ...remainingStudents,
    ];
  }
);

export default selectStudents;
